import { Box, Stack } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";

import DetailedViewPageHeader from "@/App/Layouts/PageHeader/DetailedViewPageHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useOnboardingContext } from "@/common/contexts/onboarding";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import { ViewVariant } from "@/common/ts/layout";
import {
  EntityType,
  OnboardingDto,
  OnboardingStatus,
  OnboardingStepType,
} from "@/core/api/generated/v1";

import AppButton from "../../../Button/AppButton";
import AppIconButton from "../../../Button/AppIconButton";
import AppIcon from "../../../Icons/AppIcon";
import { AppModalCloseIconButton } from "../../../Modals/AppModalCloseIconButton";
import AppTypography from "../../../Text/AppTypography";
import BaseEntityView, { BaseEntityViewInheritableProps } from "../../components/BaseEntityView";
import { OnboardingProgressIndicator } from "../OnboardingProgressIndicator";
import { OnboardingPopperMode } from "../Popper/OnboardingPopper";
import { OnboardingStepContent } from "../Step/OnboardingStepContent";
import { OnboardingStepTargetActionButton } from "../Step/OnboardingStepTargetActionButton";
import { commonViewContainerSx } from "./OnboardingView";

const defaultDisplayProps = {
  breadcrumbs: true,
  header: true,
  actions: true,
  closeInHeader: true,
  viewVariant: ViewVariant.Page,
};

interface Props extends BaseEntityViewInheritableProps<OnboardingDto> {
  stepDefinitionId: Nil<string>;
  stepType: Nil<OnboardingStepType>;
  displayProps?: Partial<typeof defaultDisplayProps>;
}

export function OnboardingStepView({ stepDefinitionId, stepType, displayProps, onClose }: Props) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const history = useAppHistory();
  const onboardingContext = useOnboardingContext();

  const stickyActionsRef = useRef<HTMLDivElement>(null);

  const stepPair = useMemo(
    () =>
      onboardingContext.getStepPairByDefinitionId(stepDefinitionId) ||
      onboardingContext.getStepPairByType(stepType),
    [onboardingContext, stepDefinitionId, stepType],
  );
  const stepDefinition = stepPair?.definition;
  const step = stepPair?.step;

  // start step if viewed
  useEffect(() => {
    if (
      stepDefinition &&
      (!step ||
        (step.status !== OnboardingStatus.InProgress && step.status !== OnboardingStatus.Completed))
    ) {
      onboardingContext.startStep(stepDefinition.id || "");
    }
  }, [onboardingContext, stepDefinition, step]);

  // toggle styles of actions when stuck/unstack on scroll
  const isStuckClassName = "Onboarding-StickyActions-IsStuck";
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        console.log(1, entry.intersectionRatio, entry);
        stickyActionsRef.current?.classList.toggle(isStuckClassName, entry.intersectionRatio < 1);
      },
      { root: null, threshold: [1] }, // trigger when fully in view vs. not
    );
    if (stickyActionsRef.current) {
      observer.observe(stickyActionsRef.current);
    }

    return () => {
      console.log(2);
      if (stickyActionsRef.current) {
        observer.unobserve(stickyActionsRef.current);
      }
    };
  }, [stickyActionsRef.current]);

  return (
    <BaseEntityView
      entityType={EntityType.Onboarding}
      entityId={onboardingContext.onboarding?.id}
      entity={onboardingContext.onboarding}
      entityRequest={undefined}
      isIniting={onboardingContext.isIniting || !onboardingContext.onboarding || !stepDefinition}
    >
      {onboardingContext.onboarding && stepDefinition && (
        <ViewLayout
          sx={{
            ...commonViewContainerSx,
          }}
          displayProps={{
            viewVariant: ViewVariant.Page,
          }}
          header={
            displayProps?.header && (
              <>
                <DetailedViewPageHeader
                  image={undefined}
                  title={<>{stepDefinition.title}</>}
                  subtitle={
                    <AppTypography variant='body1' color='textPrimary'>
                      {stepDefinition.subtitle || "Let's get started with the basics"}
                    </AppTypography>
                  }
                  startActions={
                    displayProps?.actions && (
                      <AppIconButton
                        variant='text'
                        size='medium'
                        color='text'
                        tooltipProps={{ title: "Back to checklist", isDefaultEnterDelays: true }}
                        onClick={() => {
                          history.push(ROUTE_PATH.ONBOARDING_VIEW_STEPS());
                        }}
                      >
                        <AppIcon of='goBack' />
                      </AppIconButton>
                    )
                  }
                  primaryActions={
                    displayProps?.actions &&
                    stepDefinition.isSkippable && (
                      <Box>
                        <AppButton
                          variant='text'
                          size='medium'
                          color='text'
                          onClick={async () => {
                            await onboardingContext.skipStep(stepDefinition.id || "");
                            history.push(ROUTE_PATH.ONBOARDING_VIEW_STEPS());
                          }}
                        >
                          Skip
                        </AppButton>
                      </Box>
                    )
                  }
                  closeAction={
                    displayProps.closeInHeader && onClose ? (
                      <AppModalCloseIconButton onClick={onClose} />
                    ) : undefined
                  }
                />
              </>
            )
          }
        >
          <ViewContentBlock
            sx={{
              // px: 6,
              px: 0,
              borderRadius: (th) => th.shapeCustom.borderRadiusCardLarge,
            }}
          >
            <Box>
              <Box sx={{ px: 6 }}>
                {/* Content */}
                {stepDefinition.nuclinoContent && (
                  <Box>
                    <OnboardingStepContent
                      onboardingDefinitionId={onboardingContext.onboarding?.definition?.id || ""}
                      stepDefinitionId={stepDefinition.id || ""}
                    />
                  </Box>
                )}

                {/* CTA */}
                <Box sx={{ mt: 3 }}>
                  <OnboardingStepTargetActionButton
                    stepDefinition={stepDefinition}
                    onClick={async () => {
                      // show collapsed popper so user can easily return to onboarding
                      onboardingContext.popper.setMode(OnboardingPopperMode.Collapsed);
                      onboardingContext.popper.open();
                      await onboardingContext.completeStep(stepDefinition.id || "");
                    }}
                  />
                </Box>
              </Box>

              {/* Actions */}
              <Stack
                ref={stickyActionsRef}
                data-testid='Onboarding-StickyActions'
                direction='row'
                sx={{
                  position: "sticky",
                  // trick to make it intersect 99% with the viewport when stuck and trigger IntersectionObserver
                  bottom: -1,
                  width: "100%",
                  m: 0,
                  // mx: -6,
                  pt: 3,
                  pb: 0,
                  px: 6,
                  gap: 2,
                  alignItems: "center",
                  transition: "padding 0.3s, box-shadow 0.3s",
                  backgroundColor: "transparent",
                  boxShadow: "none",

                  [`&.${isStuckClassName}`]: {
                    pb: 3,
                    backgroundColor: (th) => th.palette.background.paper,
                    boxShadow: "0 -10px 10px -10px rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                <OnboardingProgressIndicator
                  sx={{ flex: 1, maxWidth: 260 }}
                  withPercentageText={false}
                />

                <AppButton
                  sx={{ ml: "auto" }}
                  variant='contained'
                  size='medium'
                  color='primary'
                  endIcon={<AppIcon of='arrowForward' />}
                  onClick={async () => {
                    await onboardingContext.completeStep(stepDefinition.id || "");

                    const nextStepPair = onboardingContext.getNextStepPair({
                      currentStepDefinitionId: stepDefinition.id || "",
                    });
                    console.log("Next step.", { nextStepPair });
                    if (nextStepPair) {
                      history.push(
                        ROUTE_PATH.ONBOARDING_VIEW_STEP({
                          stepDefinitionId: nextStepPair.definition.id || "",
                        }),
                      );
                    } else {
                      history.push(ROUTE_PATH.ONBOARDING_VIEW_STEPS());
                    }
                  }}
                >
                  Next
                </AppButton>
              </Stack>
            </Box>
          </ViewContentBlock>
        </ViewLayout>
      )}
    </BaseEntityView>
  );
}
